import React, { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { usePollingTaskStatus } from '~/src/hooks/usePollingTaskStatus';
import { AsyncResultStatus } from '~/src/entities/project/types';
import MultiStepLoadingOverlay, {
  LoadingStep,
} from '~/src/components/MultiStepLoadingOverlay';
import PageLayout from '~/src/components/PageLayout';
import { AiGemIcon } from '~/src/components/icons';

const STEPS: LoadingStep[] = [
  { key: 'preparing', title: 'Preparing your documents...' },
  { key: 'syncing_matter', title: 'Syncing matter data from Clio Manage...' },
  {
    key: 'populating',
    title: (
      <span className="inline-flex items-center gap-2">
        <AiGemIcon className="h-5 w-5 flex-shrink-0" />
        <span>Auto-filling document fields...</span>
      </span>
    ),
  },
];

const STEP_MAPPING: Record<string, number> = {
  PENDING: 0,
  STARTED: 0,
  SYNCING_MATTER: 1,
  EXTRACTING_FIELDS: 2,
  FIELD_MAPPING: 2,
  PERSISTING: 2,
};

type PopulateFlowPollingOverlayProps = {
  taskId: string;
  onComplete: (error?: { message: string; failedStage?: string }) => void;
  pageProps?: any;
};

export const PopulateFlowPollingOverlay = ({
  taskId,
  onComplete,
  pageProps = {},
}: PopulateFlowPollingOverlayProps) => {
  const snackbar = useSnackbar();
  const taskStatusQuery = usePollingTaskStatus(taskId);

  useEffect(() => {
    const { data } = taskStatusQuery;
    if (data) {
      const { status, result } = data;
      if (status === AsyncResultStatus.SUCCESS) {
        // Check if the task succeeded but has a failed status in result
        if (
          result &&
          typeof result === 'object' &&
          'status' in result &&
          result.status === 'failed'
        ) {
          const failedStage = result.failed_stage;
          const errorMessage =
            failedStage === 'FIELD_MAPPING'
              ? 'Select the Clio Manage custom field that should be used to populate relevant document fields.'
              : result.error || 'Field mapping failed';
          onComplete({ message: errorMessage, failedStage });
        } else {
          onComplete();
        }
      } else if (status === AsyncResultStatus.FAILURE) {
        snackbar.enqueueSnackbar('Clio Manage data failed. Please try again.', {
          variant: 'error',
        });
        onComplete();
      }
    }
  }, [taskStatusQuery, onComplete, snackbar]);

  if (!taskId) {
    return null;
  }

  const currentStepIndex = taskStatusQuery.data
    ? STEP_MAPPING[taskStatusQuery.data.status] ?? 0
    : 0;

  return (
    <PageLayout {...pageProps}>
      <MultiStepLoadingOverlay
        steps={STEPS}
        currentStepIndex={currentStepIndex}
      />
    </PageLayout>
  );
};
